/**
 * URL building and HTML template generation for the chat iframe.
 * Extracted from IframeManager to keep the class focused on lifecycle.
 */
import type { OrchestratorConfig, OverlayLayout, BackendOverlayConfig } from '../types';
import { MessageSources } from '../types';

/**
 * Create iframe data URL with complete HTML document (blob URL)
 */
export function createIframeDataURL(config: OrchestratorConfig, layout: OverlayLayout, backendOverlayConfig: BackendOverlayConfig | null): string {
  const baseUrl = detectBaseUrl(config);
  const html = generateIframeHTML(config, layout, baseUrl, backendOverlayConfig);
  const blob = new Blob([html], { type: 'text/html' });
  return URL.createObjectURL(blob);
}

/**
 * Detect base URL from script source
 */
function detectBaseUrl(config: OrchestratorConfig): string {
  // Try to detect from current script
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const script = scripts[i];
    if (script.src && script.src.includes('kaigentic-chat-orchestrator')) {
      try {
        const url = new URL(script.src);
        const pathParts = url.pathname.split('/');
        pathParts.pop(); // Remove filename
        // Add assets path for ChatInterface files
        return url.origin + pathParts.join('/') + '/assets';
      } catch (e) {
        // Continue to fallback
      }
    }
  }

  // Fallback to config or current origin with assets path
  const baseUrl = config.baseUrl || window.location.origin;
  return baseUrl.endsWith('/assets') ? baseUrl : baseUrl + '/assets';
}

/**
 * Build PublicChatWidget URL for public chat mode
 */
export function buildPublicChatUrl(config: OrchestratorConfig): string {
  // If complete publicChatUrl provided, use it directly (just add query params)
  if (config.publicChatUrl) {
    const url = new URL(config.publicChatUrl);

    // Add query parameters for orchestrator integration
    url.searchParams.set('orchestrator', 'true');
    url.searchParams.set('iframeInitMode', 'true');

    if (config.debug) {
      console.log('[IframeManager] Using provided publicChatUrl:', url.toString());
    }

    return url.toString();
  }

  // Otherwise, build URL from individual parameters
  const baseUrl = config.publicChatBaseUrl || config.baseUrl || config.apiBaseUrl || window.location.origin;
  const { organizationId, tenantId, agentName, overlayName } = config;

  // Build path: /public/agent-chat/{org}/{tenant}/{agent}[/{overlay}]
  const path = `/public/agent-chat/${organizationId}/${tenantId}/${agentName}${overlayName ? '/' + overlayName : ''}`;

  const url = new URL(path, baseUrl);

  // Add query parameters for orchestrator integration
  url.searchParams.set('orchestrator', 'true');
  url.searchParams.set('iframeInitMode', 'true');

  if (config.debug) {
    console.log('[IframeManager] Built PublicChatWidget URL:', url.toString());
  }

  return url.toString();
}

/**
 * Build channel chat URL from provided channelChatUrl
 * Supports both encrypted (/chat/c/{token}) and plain (/chat/{org}/{tenant}/channel/{id}) formats
 */
export function buildChannelChatUrl(config: OrchestratorConfig): string {
  if (!config.channelChatUrl) {
    throw new Error('[IframeManager] channelChatUrl is required for channel chat mode');
  }

  // Use provided channel URL directly and append query params
  const url = new URL(config.channelChatUrl);

  // Always add orchestrator integration params
  url.searchParams.set('orchestrator', 'true');
  url.searchParams.set('iframeInitMode', 'true');

  // Add optional parameters if provided
  if (config.agentName) {
    url.searchParams.set('agent', config.agentName);
  }

  if (config.overlayName) {
    url.searchParams.set('overlay', config.overlayName);
  }

  if (config.idpHint) {
    url.searchParams.set('idp', config.idpHint);
  }

  if (config.debug) {
    console.log('[IframeManager] Built Channel Chat URL:', url.toString());
  }

  return url.toString();
}

/**
 * Get sandbox attributes for security
 */
export function getSandboxAttributes(config: OrchestratorConfig): string {
  const defaultSandbox = [
    'allow-scripts',
    'allow-same-origin',
    'allow-forms',
    'allow-popups',
    'allow-presentation',
    'allow-storage-access-by-user-activation'
  ];

  const sandboxAttrs = config.iframe?.sandbox || defaultSandbox;
  return sandboxAttrs.join(' ');
}

/**
 * Generate complete HTML document for iframe
 * Only used in webcomponent mode (when publicChatMode is false)
 */
function generateIframeHTML(
  config: OrchestratorConfig,
  layout: OverlayLayout,
  baseUrl: string,
  backendOverlayConfig: BackendOverlayConfig | null,
): string {
  const chatConfig = {
    ...config,
    forceOverlay: true, // Always use overlay mode in iframe
    overlayConfig: {
      ...layout,
      ...(config as any).overlayConfig,
      ...config.layout,
      behavior: {
        autoOpen: false, // CRITICAL: Don't auto-open in iframe - start as launcher
        autoOpenDelay: 0,
        minimizable: true,
        persistState: backendOverlayConfig?.behavior?.persistState ?? config.behavior?.persistState ?? true
      },
      persistState: false // Override layout.persistState to false - handled by handleLayoutPersistence()
    },
    // Special iframe overrides that should be applied AFTER dynamic config merge
    iframeOverrides: {
      layout: {
        launcherOffset: { x: 0, y: 0 },
        margin: 0,
        shadow: false
      },
      behavior: {
        autoOpen: false, // CRITICAL: Never auto-open in iframe mode
        autoOpenDelay: 0
      }
    }
  };

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover">
  <title>Kaigentic Chat Interface</title>
  <base href="${baseUrl}/">

  <!-- No import maps needed - using standalone bundle -->

  <!-- ChatInterface CSS -->
  <link rel="stylesheet" href="${baseUrl}/chat-interface.css">

  <!-- Iframe-specific styles -->
  <style>
    /* Only target iframe container elements, not MUI components */
    html, body {
      width: 100% !important;
      height: 100% !important;
      overflow: hidden !important;
      background: transparent !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }

    kaigentic-chat {
      display: block !important;
      width: 100% !important;
      height: 100% !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
    }
  </style>
</head>
<body>
  <!-- Use the standalone web component with iframe init mode -->
  <kaigentic-chat
    id="chat-component"
    agent-name="${chatConfig.agentName || 'demo-agent'}"
    ${chatConfig.overlayName ? `overlay-name="${chatConfig.overlayName}"` : ''}
    ${chatConfig.apiBaseUrl ? `api-base-url="${chatConfig.apiBaseUrl}"` : ''}
    ${chatConfig.token ? `token="${chatConfig.token}"` : ''}
    ${chatConfig.organizationId ? `organization-id="${chatConfig.organizationId}"` : ''}
    ${chatConfig.tenantId ? `tenant-id="${chatConfig.tenantId}"` : ''}
    ${chatConfig.overlayConfig?.theme || chatConfig.theme ? `theme="${chatConfig.overlayConfig?.theme || chatConfig.theme}"` : ''}
    force-overlay="true"
    iframe-init-mode="true"
    overlay-auto-open="false"
    overlay-minimizable="true"
    overlay-launcher-offset-x="0"
    overlay-launcher-offset-y="0"
    overlay-margin="0"
    height="100%"
    width="100%">
  </kaigentic-chat>

  <!-- Setup variables FIRST - before web component loads -->
  <script>
    window.kaigenticChatSource = 'kaigentic-chat-iframe';
  </script>

  <!-- Load standalone web component bundle -->
  <script src="${baseUrl}/kaigentic-chat-webcomponent.js"></script>

  <!-- Setup communication with parent -->
  <script>

    let readySent = false;
    const ORCHESTRATOR_ORIGIN = '${window.location.origin}';

    // Get reference to the web component
    const chatComponent = document.getElementById('chat-component');

    // Setup message bridge communication
    window.addEventListener('message', function(event) {
      // Handle messages from parent orchestrator
      if (event.data && event.data.source === '${MessageSources.ORCHESTRATOR}') {

        // Handle configuration updates
        if (event.data.type === 'update-config') {
          if (chatComponent) {
            // Update web component attributes (preserving iframe-specific overrides)
            const config = event.data.data;
            if (config.agentName) chatComponent.setAttribute('agent-name', config.agentName);
            if (config.overlayName) chatComponent.setAttribute('overlay-name', config.overlayName);
            if (config.apiBaseUrl) chatComponent.setAttribute('api-base-url', config.apiBaseUrl);
            if (config.token) chatComponent.setAttribute('token', config.token);
            if (config.organizationId) chatComponent.setAttribute('organization-id', config.organizationId);
            if (config.tenantId) chatComponent.setAttribute('tenant-id', config.tenantId);
            if (config.theme) chatComponent.setAttribute('theme', config.theme);

            // Ensure iframe-specific settings are preserved
            chatComponent.setAttribute('overlay-auto-open', 'false'); // Always false in iframe
            chatComponent.setAttribute('overlay-persist-state', 'false'); // Never persist in iframe
          }
        }

        // Handle layout updates
        if (event.data.type === 'update-layout') {
          if (chatComponent) {
            const layout = event.data.data;
            if (layout.position) chatComponent.setAttribute('overlay-position', layout.position);
            if (layout.width) chatComponent.setAttribute('overlay-chat-width', layout.width);
            if (layout.height) chatComponent.setAttribute('overlay-chat-height', layout.height);
          }
        }

        // Handle message injection
        if (event.data.type === 'inject-user-message') {
          if (chatComponent && chatComponent.injectUserMessage) {
            chatComponent.injectUserMessage(event.data.data.content);
          } else {
            console.warn('[Chat Iframe] injectUserMessage not available on chat component');
          }
        }

        if (event.data.type === 'inject-agent-response') {
          if (chatComponent && chatComponent.injectAgentResponse) {
            chatComponent.injectAgentResponse(event.data.data.content);
          } else {
            console.warn('[Chat Iframe] injectAgentResponse not available on chat component');
          }
        }

        if (event.data.type === 'clear-chat') {
          if (chatComponent && chatComponent.clearChat) {
            chatComponent.clearChat();
          } else {
            console.warn('[Chat Iframe] clearChat not available on chat component');
          }
        }

        // Handle visibility changes (web component manages its own overlay state)
        // These messages are handled by the ChatInterface internally
      }
    });

    // Setup event forwarding from web component to parent
    if (chatComponent) {
      // Forward all chat events to parent
      ['message-sent', 'message-received', 'conversation-start', 'conversation-end',
       'chat-error', 'mount-error'].forEach(eventType => {
        chatComponent.addEventListener(eventType, function(event) {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({
              source: '${MessageSources.CHAT_IFRAME}',
              type: 'chat-event',
              eventType: eventType,
              data: event.detail,
              timestamp: Date.now()
            }, ORCHESTRATOR_ORIGIN);
          }
        });
      });
    }

    // Send ready message when loaded
    function sendReadyMessage() {
      if (!readySent && window.parent && window.parent !== window) {
        readySent = true;
        window.parent.postMessage({
          source: '${MessageSources.CHAT_IFRAME}',
          type: 'ready',
          timestamp: Date.now()
        }, ORCHESTRATOR_ORIGIN);
      }
    }

    // Setup iframe initialization
    window.addEventListener('DOMContentLoaded', sendReadyMessage);
    window.addEventListener('load', sendReadyMessage);

    // Immediate ready message if DOM is already loaded
    if (document.readyState !== 'loading') {
      sendReadyMessage();
    }
  </script>
</body>
</html>`;
}
